/**
 * PillButton — the filled, fully rounded primary button (amber, or red for a
 * destructive confirm). Disabled renders the shared disabled fill with faint
 * text, so a dead button never looks tappable.
 *
 * Sizes are the four the screens actually use:
 *   sm   — 44px min-height, the type size it inherits (coming-soon "Got it",
 *          install banner "Install")
 *   md   — 50px, 14.5px (sign-out confirm)
 *   bar  — 52px, 15px, the full-width action bar (Continue, Save changes,
 *          sign-in retries)
 *   hero — 56px, 16px bold (the sign-in screen's one button)
 */
import type { JSX } from "preact";

export type PillButtonSize = "sm" | "md" | "bar" | "hero";
export type PillButtonTone = "amber" | "critical";

const SIZES: Record<PillButtonSize, JSX.CSSProperties> = {
  sm: { minHeight: "44px", borderRadius: "22px" },
  md: { height: "50px", borderRadius: "25px", fontSize: "14.5px" },
  bar: { height: "52px", borderRadius: "26px", fontSize: "15px" },
  hero: { height: "56px", borderRadius: "28px", fontSize: "16px", fontWeight: 700 },
};

const FILLS: Record<PillButtonTone, string> = {
  amber: "var(--mark-accent-amber)",
  critical: "var(--mark-status-critical)",
};

export interface PillButtonProps
  extends Omit<JSX.HTMLAttributes<HTMLButtonElement>, "type" | "size" | "style"> {
  size?: PillButtonSize;
  tone?: PillButtonTone;
  /** Block-level and the full width of its container. */
  fullWidth?: boolean;
  /** One-off additions (e.g. horizontal padding); applied last. */
  style?: JSX.CSSProperties;
}

export function PillButton({
  size = "bar",
  tone = "amber",
  fullWidth = false,
  disabled,
  style,
  ...rest
}: PillButtonProps) {
  const off = disabled === true;
  return (
    <button
      type="button"
      {...rest}
      disabled={disabled}
      style={{
        ...(fullWidth ? { display: "block", width: "100%" } : {}),
        border: "none",
        fontFamily: "inherit",
        fontWeight: 600,
        ...SIZES[size],
        cursor: off ? "default" : "pointer",
        background: off ? "var(--mark-bg-button-disabled)" : FILLS[tone],
        color: off ? "var(--mark-text-faint)" : "var(--mark-ink-on-amber)",
        ...style,
      }}
    />
  );
}
